import mapObject from "@-/com.npmjs.map-obj";
import { isAnyObject } from "@-/com.npmjs.is-what";
import type { ResolvedRouteParams } from "./types.ts";

const PERCENT_ENCODED_SEGMENT_REGEX = /%[0-9A-Fa-f]{2}/;

/**
 * Decode percent-encoding in a dynamic route segment.
 *
 * Guards against non-string input: `regex.test(value)` coerces its argument to a
 * string, and Hermes throws "Cannot determine default value of object" when that
 * argument is a plain object (no `Symbol.toPrimitive`/`toString`). Such values
 * reach here from deep links — see {@link resolveRouteParam}.
 */
export function decodeRouteParamString(value: string): string {
  if (typeof value !== "string") {
    return "";
  }
  if (!PERCENT_ENCODED_SEGMENT_REGEX.test(value)) {
    return value;
  }
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/**
 * The param value's static type is `string | string[] | undefined | null`, but at
 * runtime a deep link can put non-string values in the params bag. Expo Router
 * builds a nested navigation action for a URL like `learn/lesson/slide/0.5-pinyin`
 * and exposes the action's own control keys (`screen: string`, `params: object`,
 * `initial`/`pop`: boolean, `path: string`) through `useGlobal/LocalSearchParams`
 * alongside the real `slide-slug`. {@link resolveRouteParams} maps over *every*
 * key, so an object-valued `params` reaches here. Only strings (or the first
 * string of an array) are real route segments; anything else resolves to "".
 */
export function resolveRouteParam(raw: string | string[] | undefined | null): string {
  if (raw === undefined || raw === null) {
    return "";
  }
  const value = Array.isArray(raw) ? (raw[0] ?? "") : raw;
  if (typeof value !== "string" || value === "") {
    return "";
  }
  return decodeRouteParamString(value);
}

function isResolvedRouteParams<T extends Record<string, string | string[] | undefined | null>>(
  value: unknown,
): value is ResolvedRouteParams<T> {
  return isAnyObject(value);
}

/** Map each present route param through {@link resolveRouteParam}; nullish values stay undefined. */
export function resolveRouteParams<T extends Record<string, string | string[] | undefined | null>>(
  params: T,
): ResolvedRouteParams<T> {
  const resolved = mapObject(params, (key, value) => {
    if (value === null || value === undefined) {
      return [key, undefined];
    }

    return [key, resolveRouteParam(value)];
  });
  if (!isResolvedRouteParams<T>(resolved)) {
    throw new TypeError("Failed to resolve route params");
  }
  return resolved;
}

const GRAPHEME_PATH_SEGMENT_REGEX = /\/grapheme\/([^/]+)/;
const MORPHEME_CARD_PATH_SEGMENT_REGEX = /\/browse\/morpheme\/([^/]+)/;
const SEARCH_MORPHEME_PATH_SEGMENT_REGEX = /\/search\/card\/([^/]+)/;
const LEARN_REVIEW_MORPHEME_PATH_SEGMENT_REGEX = /\/learn\/review\/([^/]+)\/card/;
const LEARN_LEVEL_MORPHEME_PATH_SEGMENT_REGEX = /\/learn\/level\/[^/]+\/lesson\/([^/]+)/;

/**
 * Resolve a grapheme dynamic segment from local params, the pathname, then
 * optional global params. Nested child routes (e.g. `info/index`) often
 * have an empty `useLocalSearchParams()` while the `[grapheme]` param lives on
 * an ancestor, so the pathname is the stable per-screen fallback. Global params
 * are intentionally last because multiple mounted stack entries may share the
 * same route file while only one entry is globally focused.
 */
export function resolveGraphemeFromRouteSources({
  globalGrapheme,
  localGrapheme,
  pathname,
}: {
  globalGrapheme?: string | string[] | null;
  localGrapheme?: string | string[] | null;
  pathname: string;
}): string {
  const fromLocal = resolveRouteParam(localGrapheme);
  if (fromLocal !== "") {
    return fromLocal;
  }

  const learnLevelCardMatch = pathname.match(LEARN_LEVEL_MORPHEME_PATH_SEGMENT_REGEX);
  if (learnLevelCardMatch?.[1] !== undefined) {
    return decodeRouteParamString(learnLevelCardMatch[1]);
  }

  const learnReviewCardMatch = pathname.match(LEARN_REVIEW_MORPHEME_PATH_SEGMENT_REGEX);
  if (learnReviewCardMatch?.[1] !== undefined) {
    return decodeRouteParamString(learnReviewCardMatch[1]);
  }

  const cardFrontMatch = pathname.match(MORPHEME_CARD_PATH_SEGMENT_REGEX);
  if (cardFrontMatch?.[1] !== undefined) {
    return decodeRouteParamString(cardFrontMatch[1]);
  }

  const searchCardFrontMatch = pathname.match(SEARCH_MORPHEME_PATH_SEGMENT_REGEX);
  if (searchCardFrontMatch?.[1] !== undefined) {
    return decodeRouteParamString(searchCardFrontMatch[1]);
  }

  const match = pathname.match(GRAPHEME_PATH_SEGMENT_REGEX);
  if (match?.[1] !== undefined) {
    return decodeRouteParamString(match[1]);
  }

  const fromGlobal = resolveRouteParam(globalGrapheme);
  if (fromGlobal !== "") {
    return fromGlobal;
  }

  return "";
}
